import React, { ReactNode } from 'react'
import { Box, Container, Card, Typography } from '@mui/material'
import type { Breakpoint } from '@mui/material/styles'
import { poolwerxColors } from '../../theme/colors'
import { InspectorIcon } from '../SystemIcons'
import poolwerxLogo from '../../assets/pwx-logo-evo-platform-trans.png'

// Shared standalone-card design tokens. These are the single source of truth
// for the Login, Page Unavailable and What's New cards - keep them here so the
// three pages stay visually consistent (radius, shadow, header height, divider).
const CARD_RADIUS = 12
const CARD_SHADOW = '0 4px 20px rgba(0, 0, 0, 0.06)'
const HEADER_MIN_HEIGHT = 64
// Subtle neutral hairline under the teal header, replacing the old harsh 3px
// red divider. A faint translucent white line reads cleanly against both the
// teal header above and the white card body below.
const HEADER_DIVIDER = '1px solid rgba(255, 255, 255, 0.25)'

interface StandaloneCardLayoutProps {
  headerTitle: string
  headerSubtitle?: ReactNode  // For displaying below Inspector Tool in card body
  headerIcon?: ReactNode
  children: ReactNode
  showHeader?: boolean // Allow hiding header for special cases
  franchiseDetails?: {
    name: string
    location: string
  }
  /**
   * Max width of the centred card. Accepts an MUI breakpoint (`'sm'`, `'lg'`,
   * ...), a pixel number, or `false` to disable the cap. Defaults to `'sm'`
   * (~600px) - the standard width for the auth / utility cards. Pass a wider
   * value for content-heavy screens (e.g. What's New `1000`).
   */
  containerMaxWidth?: Breakpoint | number | false
}

const StandaloneCardLayout: React.FC<StandaloneCardLayoutProps> = ({
  headerTitle,
  headerSubtitle,
  headerIcon,
  children,
  showHeader = true,
  franchiseDetails,
  containerMaxWidth = 'sm',
}) => {
  const isNumericMaxWidth = typeof containerMaxWidth === 'number'
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: 2,
        paddingTop: '20px',  // Added 20px margin at the top
      }}
    >
      {/* Poolwerx Logo */}
      <img
        src={poolwerxLogo}
        alt="Poolwerx"
        style={{
          height: '60px',
          marginBottom: '34px'
        }}
      />

      {/* Main Card Container */}
      <Container
        maxWidth={isNumericMaxWidth ? false : (containerMaxWidth as Breakpoint | false)}
        sx={isNumericMaxWidth ? { maxWidth: containerMaxWidth as number } : undefined}
      >
        <Card
          elevation={0}
          sx={{
            borderRadius: `${CARD_RADIUS}px`,
            boxShadow: CARD_SHADOW,
            overflow: 'hidden'  // Clip the gradient header to the rounded top corners
          }}
        >
          {/* Teal Header Block with Inspector Tool */}
          {showHeader && (
            <Box
              sx={{
                backgroundImage: `linear-gradient(180deg, ${poolwerxColors.teal} 0%, ${poolwerxColors.darkCyan} 100%)`,
                color: 'white',
                minHeight: HEADER_MIN_HEIGHT,
                px: 2,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                borderBottom: HEADER_DIVIDER,
                // Top corners follow the card radius; bottom corners stay square.
                borderTopLeftRadius: CARD_RADIUS,
                borderTopRightRadius: CARD_RADIUS
              }}
            >
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'white',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 700,
                  fontSize: '1.31rem'  // 20% smaller than 1.638rem
                }}
              >
                <InspectorIcon size={22} />
                <Box component="span" sx={{ position: 'relative', top: '2px', ml: '3px' }}>
                  Inspector Tool
                </Box>
              </Typography>
            </Box>
          )}

          {/* Card Content */}
          <Box sx={{ padding: 4 }}>
            {/* Header Title and Details (previously in teal header) */}
            {(headerTitle || franchiseDetails) && (
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                {headerIcon && (
                  <Box sx={{ mb: 2, color: poolwerxColors.teal }}>
                    {headerIcon}
                  </Box>
                )}
                <Typography variant="h5" fontWeight="bold" sx={{ color: poolwerxColors.teal, mb: 1 }}>
                  {headerTitle}
                </Typography>
                {franchiseDetails && (
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: poolwerxColors.charcoal }}>
                      {franchiseDetails.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: poolwerxColors.grey[600], mt: 0.5 }}>
                      {franchiseDetails.location}
                    </Typography>
                  </Box>
                )}
                {headerSubtitle && (
                  <Box sx={{ mt: 2 }}>
                    {headerSubtitle}
                  </Box>
                )}
              </Box>
            )}

            {children}
          </Box>
        </Card>
      </Container>
    </Box>
  )
}

export default StandaloneCardLayout
